import * as Sentry from "@sentry/react";
import type { ColumnDef } from "@tanstack/react-table";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc
} from "firebase/firestore";
import { BellRing, Pencil, Plus, Trash2 } from "lucide-react";
import { useEffect, useState, type FormEvent, type ReactNode } from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/ui/page-header";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";

import { db } from "../../../api/firebaseApi";
import { VISAVALUE } from "../../../types/visa.type";

const FormField = ({
  label,
  htmlFor,
  children
}: {
  label: string;
  htmlFor?: string;
  children: ReactNode;
}) => (
  <div className="flex min-w-55 flex-1 flex-col gap-1.5">
    <Label htmlFor={htmlFor}>{label}</Label>
    {children}
  </div>
);

const ChoiceField = ({
  label,
  placeholder,
  value,
  options,
  onValueChange
}: {
  label: string;
  placeholder: string;
  value: string;
  options: string[];
  onValueChange: (value: string) => void;
}) => (
  <FormField label={label}>
    <Select value={value || undefined} onValueChange={onValueChange}>
      <SelectTrigger className="w-full" aria-label={label}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o} value={o}>
            {o}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </FormField>
);

enum draft_type {
  PACKET = "packet",
  EXPERT_LETTERS = "expert_letters",
  LEGAL_BRIEF = "legal_brief",
  COVER_LETTER = "cover_letter",
  EXHIBIT_LIST = "exhibit_list"
}
type DraftRowData = {
  description?: string;
  visa?: string;
  draftType?: string;
  practiceName?: string;
  customer_id?: string;
};

type Row = { id: string; data: DraftRowData };

// Admin-style component aligned with your import/JSX patterns from AdminUsers.
export const AdminUsageMonitoringNotifications = ({ pageSize = 100 }) => {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState({
    description: "",
    visa: "",
    draftType: "",
    practiceName: "",
    customer_id: ""
  });
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [mode, setMode] = useState("add"); // "add" | "edit"

  const [formOpen, setFormOpen] = useState(false);
  const addEditModal = {
    onOpen: () => setFormOpen(true),
    onClose: () => setFormOpen(false)
  };

  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    const q = query(collection(db, "usage_monitoring_notifications"));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const next = snap.docs.map((d) => ({
          id: d.id,
          data: d.data() as DraftRowData
        }));
        setRows(next);
        setLoading(false);
      },
      (err) => {
        Sentry.captureException(err);
        setError(err.message || "Failed to fetch notifications");
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  const openAdd = () => {
    setMode("add");
    setEditingId(null);
    setForm({
      description: "",
      visa: "",
      draftType: "",
      practiceName: "",
      customer_id: ""
    });
    addEditModal.onOpen();
  };

  const openEdit = (id: string, data: DraftRowData) => {
    setMode("edit");
    setEditingId(id);
    setForm({
      description: data?.description || "",
      visa: data?.visa || "",
      draftType: data?.draftType || "",
      practiceName: data?.practiceName || "",
      customer_id: data?.customer_id || ""
    });
    addEditModal.onOpen();
  };

  const onChange = (k: keyof typeof form, v: string) =>
    setForm((prev) => ({ ...prev, [k]: v }));

  const handleSubmit = async (e?: FormEvent) => {
    e?.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const payload = {
        ...Object.fromEntries(
          Object.entries(form).map(([k, v]) => [k, v === "" ? null : v])
        ),
        updatedAt: serverTimestamp()
      };
      if (mode === "add") {
        await addDoc(collection(db, "usage_monitoring_notifications"), {
          ...payload,
          createdAt: serverTimestamp()
        });
      } else if (mode === "edit" && editingId) {
        await updateDoc(
          doc(db, "usage_monitoring_notifications", editingId),
          payload
        );
      }
      addEditModal.onClose();
    } catch (err) {
      Sentry.captureException(err);
      setError(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const requestDelete = (id: string) => setPendingDeleteId(id);

  const confirmDelete = async () => {
    if (!pendingDeleteId) return;
    try {
      await deleteDoc(
        doc(db, "usage_monitoring_notifications", pendingDeleteId)
      );
    } catch (err) {
      Sentry.captureException(err);
      setError(err instanceof Error ? err.message : "Failed to delete");
    } finally {
      setPendingDeleteId(null);
    }
  };

  const muted = (v?: string) => (
    <span className="text-base text-fg-2">{v || "—"}</span>
  );

  const columns: ColumnDef<Row, unknown>[] = [
    {
      id: "description",
      header: "Description",
      cell: ({ row }) => (
        <span className="text-base break-words whitespace-pre-wrap text-fg">
          {row.original.data.description || "—"}
        </span>
      )
    },
    {
      id: "visa",
      header: "Visa",
      cell: ({ row }) => muted(row.original.data.visa)
    },
    {
      id: "draftType",
      header: "Draft Type",
      cell: ({ row }) => muted(row.original.data.draftType)
    },
    {
      id: "practice",
      header: "Practice Name",
      cell: ({ row }) => muted(row.original.data.practiceName)
    },
    {
      id: "customer",
      header: "Customer ID",
      cell: ({ row }) => muted(row.original.data.customer_id)
    },
    {
      id: "id",
      header: "ID",
      cell: ({ row }) => (
        <span className="font-mono text-sm text-fg-3">{row.original.id}</span>
      )
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => (
        <div className="flex justify-end gap-1">
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Edit"
            onClick={() => openEdit(row.original.id, row.original.data)}
          >
            <Pencil />
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Delete"
            className="text-danger"
            onClick={() => requestDelete(row.original.id)}
          >
            <Trash2 />
          </Button>
        </div>
      )
    }
  ];

  return (
    <div className="flex h-full min-h-0 w-full flex-col overflow-hidden bg-surface">
      <div className="flex shrink-0 flex-col gap-4 px-6 pt-6 pb-3">
        <PageHeader
          title="Usage Monitoring Notifications"
          count={rows.length || null}
          actions={
            <Button size="sm" onClick={openAdd}>
              <Plus /> New Notification
            </Button>
          }
        />
        {error ? (
          <div
            role="alert"
            className="rounded-md bg-danger-soft px-3 py-2 text-base text-danger ring-1 ring-danger-line"
          >
            {error}
          </div>
        ) : null}
      </div>
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        {!loading && rows.length === 0 ? (
          <EmptyState
            icon={BellRing}
            title="No Notifications"
            description="Use New Notification to add one."
            className="py-16"
          />
        ) : (
          <DataTable
            data={rows.slice(0, pageSize)}
            columns={columns}
            getRowId={(r) => r.id}
            isLoading={loading}
            className="overflow-visible"
          />
        )}
      </div>

      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="sm:max-w-160">
          <DialogHeader>
            <DialogTitle>
              {mode === "add" ? "New Notification" : "Edit Notification"}
            </DialogTitle>
          </DialogHeader>
          <form
            id="usage-monitoring-form"
            className="flex flex-col gap-4"
            onSubmit={handleSubmit}
          >
            <FormField label="Description" htmlFor="usage-description">
              <Textarea
                id="usage-description"
                value={form.description || ""}
                onChange={(e) => onChange("description", e.target.value)}
                placeholder="This will appear in the alert slack"
                className="min-h-24"
              />
            </FormField>
            <div className="flex flex-wrap gap-4">
              <ChoiceField
                label="Visa"
                placeholder="Select visa type"
                value={form.visa}
                options={Object.values(VISAVALUE)}
                onValueChange={(v) => onChange("visa", v)}
              />
              <ChoiceField
                label="Draft Type"
                placeholder="Select draft type"
                value={form.draftType}
                options={Object.values(draft_type)}
                onValueChange={(v) => onChange("draftType", v)}
              />
              <FormField label="Practice Name" htmlFor="usage-practice">
                <Input
                  id="usage-practice"
                  value={form.practiceName || ""}
                  onChange={(e) => onChange("practiceName", e.target.value)}
                />
              </FormField>
              <FormField label="Customer ID" htmlFor="usage-customer">
                <Input
                  id="usage-customer"
                  value={form.customer_id || ""}
                  onChange={(e) => onChange("customer_id", e.target.value)}
                />
              </FormField>
            </div>
          </form>
          <DialogFooter>
            <Button
              variant="secondary"
              size="sm"
              onClick={addEditModal.onClose}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              type="submit"
              form="usage-monitoring-form"
              disabled={saving}
            >
              {saving ? <Spinner /> : null}
              {mode === "add" ? "Create" : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={pendingDeleteId !== null}
        onOpenChange={(open) => !open && setPendingDeleteId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Notification</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the document. This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction variant="destructive" onClick={confirmDelete}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
